import {
  useCustomMutation,
  useCustomQuery,
  useDeleteMutation,
} from '@gripp/shared-logic';
import {
  Colors,
  ControlledTextInput,
  LoadingIndicator,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useAutoFocusRef,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';

type Props = {
  onComplete: () => void;
  workspaceId: string;
  addonId: string;
};

export const EditAddonForm = ({ workspaceId, addonId, onComplete }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useCustomMutation({
    query: UPDATE_ADDON,
  });

  const { data: addonData, isPending: isAddonLoading } = useCustomQuery({
    query: FIND_ADDON,
    cacheKey: ['addon', addonId],
    variables: { id: addonId },
  });

  const { mutateAsync: deleteAddOn } = useDeleteMutation({
    modelName: 'addon',
    query: DELETE_ADDON,
  });

  const addon = addonData?.getAddon;

  const formSchema = yup.object({
    name: yup.string().required(t('addOn.create.validation.name_required')),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (addon?.name) {
      reset({ name: addon.name });
    }
  }, [addon, reset]);

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input = {
      id: addonId,
      name: data.name,
      workspace: { id: workspaceId },
    };
    await mutateAsync(
      { input },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['addon'] });
          onComplete();
        },
      }
    );
  };

  const elementFocusCallBack = useAutoFocusRef();

  if (isAddonLoading) {
    return <LoadingIndicator flexContainer />;
  }

  const deleteAddon = async () => {
    const input = {
      id: addonId,
    };

    await deleteAddOn(
      { input },
      {
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: ['addon'] });
          onComplete();
        },
      }
    );
  };

  return (
    <View style={styles.contentContainer}>
      <SaveFormTop
        isDirty={isDirty && !isPending}
        isValid={isValid && !isPending}
        onSubmit={handleSubmit(onSubmit)}
        title={t('addOn.edit.label')}
        saveLabel={t('addOn.edit.save')}
        topBar={TopModalBar}
        onCancel={onComplete}
      >
        <View style={styles.formItem}>
          <View>
            <Text style={styles.formItemTitle}>{t('addOn.list.name')}</Text>
          </View>
          <ControlledTextInput
            control={control}
            name="name"
            placeholder={t('addOn.edit.placeholder')}
            innerRef={elementFocusCallBack}
          />
        </View>
      </SaveFormTop>
      <View style={styles.deleteContainer}>
        <Pressable
          onPress={deleteAddon}
          hitSlop={5}
          style={styles.deleteAddonButton}
        >
          <Text allowFontScaling={false} style={styles.deleteLabel}>
            {t('addOn.deleteAddon')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    width: '100%',
  },
  header: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.basePadding.paddingVertical,
  },
  deleteContainer: {
    marginTop: 20,
    paddingHorizontal: 20,
    borderColor: Colors.graySpacer,
    borderTopWidth: 1,
  },
  deleteAddonButton: {
    width: 150,
    height: 25,
    marginTop: 10,
  },
  deleteLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.red,
    letterSpacing: -0.25,
  },
});

const DELETE_ADDON = `
  mutation deleteAddon($input: MutateAddonInput!) {
    deleteAddon(input: $input) {
      id
    }
  }
`;

const FIND_ADDON = `
  query FindAddon($id: ID!) {
    getAddon(id: $id) {
      id
      name
    }
  }
`;

const UPDATE_ADDON = `
  mutation UpdateAddon($input: MutateAddonInput!) {
    updateAddon(input: $input) {
      id
      name
      workspace {
        id
        name
      }
    }
  }
`;
